import styles from './Mobile.module.scss';

import { RouteFC } from 'util/routeTypes';
import { Router, Redirect } from '@reach/router';
import { useTranslation } from 'react-i18next';
import React, { FormEventHandler, memo, useCallback, useEffect } from 'react';
import { ContactInfoForm } from 'components/ContactInfoForm';
import { ReviewAndPayment } from '../ReviewAndPayment/ReviewAndPayment';
import { Button } from '@clover/web-lib-consumer-ui';
import { useCheckoutValidity } from '../context/CheckoutValidity';
import { CHECKOUT_TEST_ID } from '../const';
import { useSubmitOrder } from '../context/SubmitOrder/SubmitOrder';
import { PageLayout } from 'components/PageLayout';
import { MobilePlaceOrderButton } from '../PlaceOrderButton/PlaceOrderButton';
import { CheckoutHeader } from 'routes/Checkout/CheckoutHeader';
import { Icon } from 'components/Icon';
import { CUSTOMER_INFO_TEST_ID } from 'components/ContactInfoForm.const';
import { ScrollToTop } from 'components/ScrollToTop';
import { EditGiftCardModal } from 'routes/GiftCard/Modal';
import { EditMenuItemModal } from 'routes/MenuItemModal/EditMenuItemModal';
import { MerchantTitle } from 'components/MerchantTitle';
import { DeliveryAddressModal } from 'routes/DeliveryAddressModal/DeliveryAddressModal';
import { DeliveryModalError } from 'routes/DeliveryAddressModal/components/DeliveryModalError';
import { CheckoutAuthentication } from 'routes/Checkout/Authentication/CheckoutAuthentication';
import { useServiceType } from 'hooks/context/serviceType';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';
import { useGetLatestAddress } from 'hooks/useApi/useAddress';
import { useCustomer } from 'hooks/context/customer/customer';
import { useOrderTotals } from '../context/useOrderTotals';

const InfoRoute: RouteFC = memo(function InfoRoute({ navigate }) {
  const { t } = useTranslation();
  const { isContactFormDataValid } = useCheckoutValidity();

  const { isLoggedIn } = useCustomer();
  const { hasDeliveryAddress, currentServiceType, isDeliveryChosen } = useServiceType();
  const serverDefaultAddress = useGetLatestAddress();
  const defaultAddress = serverDefaultAddress.data;
  const { asyncValidateAndSetDefaultAddress } = useDeliveryValidation();
  const { isRedirectAddressModal, setRedirectFromAddressModal } = useServiceType();

  useEffect(() => {
    if (isDeliveryChosen) {
      if (defaultAddress && hasDeliveryAddress && isRedirectAddressModal) {
        asyncValidateAndSetDefaultAddress(defaultAddress, { replacePath: true }, currentServiceType);
      } else if (hasDeliveryAddress && !isLoggedIn && isRedirectAddressModal) {
        navigate?.('../address');
      } else if (hasDeliveryAddress && isLoggedIn && isRedirectAddressModal) {
        navigate?.('../address');
      }
    }
    setRedirectFromAddressModal(true);
  }, [defaultAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      navigate?.('../review');
    },
    [navigate]
  );

  return (
    <ScrollToTop>
      <MerchantTitle i18nKey="helmet.titleCheckoutInfo" />
      <form className={styles.wrapper} onSubmit={onSubmit}>
        <ContactInfoForm />
        <Button
          type="submit"
          data-testid={CUSTOMER_INFO_TEST_ID.submitButton}
          className={styles.button}
          disabled={!isContactFormDataValid}
        >
          <span>{t('checkoutPage.mobile.continue')}</span>
          <Icon className={styles.arrowIcon} name="leftArrow" />
        </Button>
      </form>
    </ScrollToTop>
  );
});

const ReviewRoute: RouteFC = memo(function ReviewRoute() {
  const { submit } = useSubmitOrder();
  const { totalWithTip } = useOrderTotals();
  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    submit(totalWithTip);
  };

  return (
    <>
      <MerchantTitle i18nKey="helmet.titleCheckoutSummary" />
      <Router primary={false}>
        <EditGiftCardModal path="gift-card/:index" />
        <EditMenuItemModal path="item/:cartId" />
        <DeliveryModalError path="address-error" />
      </Router>
      <ScrollToTop>
        <form className={styles.wrapper} onSubmit={handleSubmit} data-testid={CHECKOUT_TEST_ID.form}>
          <ReviewAndPayment />
          <MobilePlaceOrderButton type="submit" className={styles.button} />
        </form>
      </ScrollToTop>
    </>
  );
});

export const Mobile: React.FC = memo(function Mobile() {
  return (
    <PageLayout header={<CheckoutHeader className={styles.header} />} customMainClass={styles.main}>
      <Router>
        <ReviewRoute path="review/*" />
        <InfoRoute path="info" />
        <DeliveryModalError path="address-error" />
        <DeliveryAddressModal path="address" />
        <CheckoutAuthentication path="login/*" />
        <Redirect from="*" to="info" noThrow />
      </Router>
    </PageLayout>
  );
});
